import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';

const fmtSize = (b) => {
  if (!b) return '';
  if (b > 1e9) return `${(b / 1e9).toFixed(2)} GB`;
  if (b > 1e6) return `${(b / 1e6).toFixed(1)} MB`;
  return `${(b / 1024).toFixed(0)} KB`;
};

const fmtDur = (s) => {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
};

const statusStyle = {
  scheduled:   { bg: 'rgba(245,158,11,0.10)', color: 'var(--accent-a)', label: 'Agendado' },
  downloading: { bg: 'rgba(67,97,238,0.10)',  color: 'var(--accent)',   label: 'Baixando' },
  ok:          { bg: 'rgba(6,185,111,0.10)',  color: 'var(--accent-g)', label: 'Pronto' },
  error:       { bg: 'rgba(239,68,68,0.10)',  color: 'var(--accent-r)', label: 'Erro' },
};

function StatusChip({ status }) {
  const st = statusStyle[status] || statusStyle.error;
  return (
    <span style={{ padding: '2px 8px', background: st.bg, color: st.color, fontSize: 10, borderRadius: 10, fontWeight: 600 }}>
      {st.label}
    </span>
  );
}

export default function VslDownloader() {
  const [url, setUrl] = useState('');
  const [downloadAll, setDownloadAll] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const load = () => {
    apiFetch('/api/vsl-download').then(d => setJobs(d.jobs || [])).catch(() => {});
  };

  useEffect(() => {
    load();
    // Polling continuo enquanto houver job em andamento
    pollRef.current = setInterval(() => {
      setJobs(prev => {
        if (prev.some(j => j.status === 'scheduled' || j.status === 'downloading')) load();
        return prev;
      });
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    const u = url.trim();
    if (!u) return;
    setSubmitting(true); setError(null);
    try {
      await apiFetch('/api/vsl-download', { method: 'POST', body: JSON.stringify({ url: u, all: downloadAll }) });
      setUrl('');
      load();
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={s.page}>
      <div style={s.topBar}>
        <div style={s.title}>Baixar VSL</div>
        <div style={s.subtitle}>Cole a URL de qualquer landing page com VSL (Vturb / player HLS). O sistema baixa o vídeo em alta qualidade (MP4 concatenado dos segments).</div>
      </div>

      <form style={s.form} onSubmit={submit}>
        <input type="url" placeholder="https://landing-page.com/oferta?utm_source=..." value={url}
               onChange={e => setUrl(e.target.value)} style={s.input} required />
        <label style={s.allLabel} title="Baixa TODAS as VSLs interceptadas na pagina (util pra LPs com upsell + VSL principal). Sem isso, so a VSL 'principal' e baixada.">
          <input type="checkbox" checked={downloadAll} onChange={e => setDownloadAll(e.target.checked)} style={{ marginRight: 6 }} />
          Baixar todas
        </label>
        <button type="submit" disabled={submitting} style={{ ...s.btn, opacity: submitting ? 0.5 : 1 }}>
          {submitting ? '...' : 'Baixar'}
        </button>
      </form>
      {error && <div style={s.error}>{error}</div>}

      <div style={s.hint}>
        Dica: cole a URL COMPLETA (com <code>fbclid</code>, <code>utm_*</code>, etc). LPs com cloaker retornam versão diferente sem esses params.
      </div>

      <div style={s.section}>
        <div style={s.sectionTitle}>Últimos jobs ({jobs.length})</div>
        {jobs.length === 0 && <div style={s.dim}>Nenhum download ainda.</div>}
        <table style={s.table}>
          <thead>
            <tr>
              <th style={s.th}>Status</th>
              <th style={s.th}>URL</th>
              <th style={{ ...s.th, width: 90, textAlign: 'right' }}>Duração</th>
              <th style={{ ...s.th, width: 90, textAlign: 'right' }}>Tamanho</th>
              <th style={{ ...s.th, width: 220, textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map(j => {
              // paths[]: multi-VSL. Se vazio mas path (compat), transforma em [{path}]
              const items = (j.paths && j.paths.length > 0)
                ? j.paths
                : (j.path ? [{ path: j.path, size_bytes: j.size_bytes, duration_s: j.duration_s }] : []);
              const label = j.download_all && j.total_masters ? ` (${items.length}/${j.total_masters} VSLs)` : '';
              return (
                <tr key={j.job_id}>
                  <td style={s.td}><StatusChip status={j.status} />{label && <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2 }}>{label}</div>}</td>
                  <td style={{ ...s.td, wordBreak: 'break-all', color: 'var(--text2)' }}>
                    <a href={j.url} target="_blank" rel="noopener" style={s.link} title={j.url}>
                      {j.url.length > 80 ? j.url.slice(0, 80) + '…' : j.url}
                    </a>
                    {j.error && <div style={{ ...s.errBox, marginTop: 6, fontSize: 10 }}>{j.error}</div>}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {items.length === 1 ? (items[0].duration_s ? fmtDur(items[0].duration_s) : '—') : (items.length > 1 ? `${items.length}x` : '—')}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: 'var(--accent-g)' }}>
                    {fmtSize(j.size_bytes) || '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>
                    {j.status === 'ok' && items.length > 0 && items.map((it, i) => {
                      const fn = it.path.split('/').pop();
                      return (
                        <div key={i} style={{ marginBottom: i < items.length - 1 ? 4 : 0 }}>
                          {items.length > 1 && <span style={{ fontSize: 10, color: 'var(--text3)', marginRight: 6 }}>#{i+1} {fmtDur(it.duration_s)} · {fmtSize(it.size_bytes)}</span>}
                          <a href={it.path} target="_blank" rel="noopener" style={s.actLink}>Preview</a>
                          <a href={it.path} download={fn} style={{ ...s.actBtn, marginLeft: 6 }}>MP4</a>
                        </div>
                      );
                    })}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const s = {
  page: { padding: 24, maxWidth: 1400, margin: '0 auto' },
  topBar: { marginBottom: 16 },
  title: { fontSize: 18, fontWeight: 700, color: 'var(--text)' },
  subtitle: { fontSize: 12, color: 'var(--text3)', marginTop: 4, maxWidth: 720 },
  form: { display: 'flex', gap: 8, marginBottom: 8 },
  input: { flex: 1, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 6, background: 'var(--surface2)', color: 'var(--text)', fontSize: 13, fontFamily: 'inherit', outline: 'none' },
  btn: { padding: '10px 22px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  allLabel: { display: 'inline-flex', alignItems: 'center', padding: '10px 12px', color: 'var(--text2)', fontSize: 12, cursor: 'pointer', userSelect: 'none' },
  hint: { fontSize: 11, color: 'var(--text3)', marginBottom: 20 },
  error: { padding: '8px 12px', background: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)', borderRadius: 6, fontSize: 12, marginBottom: 8 },
  errBox: { padding: '4px 8px', background: 'rgba(239,68,68,0.10)', color: 'var(--accent-r)', borderRadius: 4, fontSize: 11 },
  section: { marginTop: 20 },
  sectionTitle: { fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 },
  dim: { fontSize: 12, color: 'var(--text3)', padding: 20 },
  table: { width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6 },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 10, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: '1px solid var(--border)', fontWeight: 600 },
  td: { padding: '10px 12px', borderBottom: '1px solid var(--surface3)', fontSize: 12, color: 'var(--text)' },
  link: { color: 'var(--accent)', textDecoration: 'none' },
  actLink: { color: 'var(--accent)', textDecoration: 'none', fontSize: 11 },
  actBtn: { padding: '4px 10px', background: 'var(--accent-g)', color: '#fff', textDecoration: 'none', borderRadius: 4, fontSize: 11, fontWeight: 600 },
};
